import React from 'react';
import { AlertCircle, RefreshCw, Trash2, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Actualizar el estado para que la próxima renderización muestre la UI de fallback
    return { 
      hasError: true, 
      error
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log del error para debugging
    console.error('❌ Error capturado por Error Boundary:', error);
    console.error('📋 Información del error:', errorInfo);
    
    // Guardar información del error en el estado
    this.setState(prevState => ({
      error,
      errorInfo: errorInfo?.componentStack || null,
      errorCount: (prevState.errorCount || 0) + 1
    }));

    // Si es un error relacionado con carga de módulos, intentar recuperación automática
    if (this.isModuleLoadError(error)) {
      console.warn('⚠️ Error de carga de módulo detectado, intentando recuperación...');
      setTimeout(() => {
        this.handleClearCache();
      }, 2000);
    }
  }

  // Verificar si el error es relacionado con carga de módulos/chunks
  isModuleLoadError(error) {
    if (!error || !error.message) return false;
    
    const message = error.message.toLowerCase();
    return (
      message.includes('loading chunk') ||
      message.includes('chunk load failed') ||
      message.includes('failed to fetch') ||
      message.includes('networkerror') ||
      message.includes('404') ||
      message.includes('loading css chunk') ||
      (message.includes('script error') && message.includes('static'))
    );
  }

  // Verificar si podemos recargar (evitar bucle infinito)
  canReload = () => {
    try {
      const raw = localStorage.getItem('_errorReloadGuard');
      const data = raw ? JSON.parse(raw) : { count: 0, time: 0 };
      if (data.count >= 2) return false;
      if (Date.now() - data.time < 60000) return false;
      return true;
    } catch { return true; }
  };

  recordReload = () => {
    try {
      const raw = localStorage.getItem('_errorReloadGuard');
      const data = raw ? JSON.parse(raw) : { count: 0, time: 0 };
      data.count = (data.count || 0) + 1;
      data.time = Date.now();
      localStorage.setItem('_errorReloadGuard', JSON.stringify(data));
    } catch {}
  };

  // Limpiar caché del navegador
  handleClearCache = async () => {
    if (!this.canReload()) return;
    this.recordReload();
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      localStorage.removeItem('frontendVersion');
      localStorage.removeItem('ignoredVersion');
      sessionStorage.clear();
      window.location.reload(true);
    } catch (error) {
      if (this.canReload()) window.location.reload(true);
    }
  };

  // Recargar sin limpiar caché
  handleReload = () => {
    window.location.reload();
  };

  // Ir al inicio
  handleGoHome = () => {
    window.location.href = '/';
  };

  // Resetear error boundary (intentar renderizar de nuevo)
  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      const isModuleError = this.state.error && this.isModuleLoadError(this.state.error);
      
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            {/* Icono de Error */}
            <div className="flex justify-center mb-4">
              <div className="bg-red-100 rounded-full p-3">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
            </div>

            {/* Título */}
            <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">
              Algo salió mal
            </h2>

            {/* Mensaje según el tipo de error */}
            {isModuleError ? (
              <div className="mb-4">
                <p className="text-gray-600 text-center mb-2">
                  Se detectó un problema al cargar los archivos de la aplicación.
                </p>
                <p className="text-sm text-gray-500 text-center">
                  Esto generalmente ocurre cuando hay una actualización disponible. 
                  Estamos limpiando el caché y recargando automáticamente...
                </p>
              </div>
            ) : (
              <p className="text-gray-600 text-center mb-4">
                La aplicación encontró un error inesperado. Por favor, intenta una de las opciones siguientes:
              </p>
            )}

            {/* Información de error (solo en desarrollo) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-4 p-3 bg-gray-100 rounded-lg text-xs text-gray-700 font-mono overflow-auto max-h-32">
                <div className="font-semibold mb-1">Error:</div>
                <div>{this.state.error.toString()}</div>
                {this.state.errorInfo && (
                  <>
                    <div className="font-semibold mt-2 mb-1">Stack:</div>
                    <div className="text-xs">{this.state.errorInfo}</div>
                  </>
                )}
              </div>
            )}

            {/* Botones de acción */}
            <div className="space-y-2">
              {isModuleError ? (
                <button
                  onClick={this.handleClearCache}
                  className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 font-medium"
                >
                  <RefreshCw className="h-5 w-5" />
                  <span>Limpiar Caché y Recargar</span>
                </button>
              ) : (
                <>
                  <button
                    onClick={this.handleReset}
                    className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 font-medium"
                  >
                    <RefreshCw className="h-5 w-5" />
                    <span>Intentar de Nuevo</span>
                  </button>

                  <button
                    onClick={this.handleClearCache}
                    className="w-full bg-orange-600 text-white px-4 py-3 rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2 font-medium"
                  >
                    <Trash2 className="h-5 w-5" />
                    <span>Limpiar Caché y Recargar</span>
                  </button>

                  <button
                    onClick={this.handleReload}
                    className="w-full bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2 font-medium"
                  >
                    <RefreshCw className="h-5 w-5" />
                    <span>Recargar Página</span>
                  </button>
                </>
              )}

              <button
                onClick={this.handleGoHome}
                className="w-full bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2 font-medium"
              >
                <Home className="h-5 w-5" />
                <span>Ir al Inicio</span>
              </button>
            </div>

            {/* Mensaje de ayuda */}
            <p className="text-xs text-gray-500 text-center mt-4">
              Si el problema persiste, contacta al administrador del sistema.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

